import { forwardRef, useEffect, useMemo, useState } from "react";
import { formatDate, formatMoney, getBranchLogoUrl, getLogoUrl, getRealFileName } from "src/Common/common";
import { paymentItems } from "src/Common/contants";
import { resolveBrand } from "./brand";

/**
 * The printed Purchase Order, as DOM.
 *
 * This is the single source of truth for both Preview and Download: the modal
 * shows this element, and `generateFormPdf` rasterises the very same node, so
 * what the user previews is exactly what they get.
 *
 * Two variants mirror the two creation modes:
 *   detailed - itemised, with the line-item table and Total.
 *   simple   - receipt-style, with a free-text Description.
 *
 * The Note field is deliberately never rendered — it is internal-only.
 *
 * Optional blocks (quote number, ship-to, per-line qty/rate) render when
 * present and are skipped when absent — POs created before those fields
 * existed simply print without them.
 */

// Neutral palette, shared by both brands. Only the accent differs.
const INK = "#1f2a37";
const MUTED = "#6b7688";
const LINE = "#d5dae2";
const SHADE = "#f4f6f8";

// The server computes and stores each line's amount; recomputing from qty and
// rate is the fallback for a PO saved before it did.
const lineAmount = (item) => {
  if (item?.amount != null && item.amount !== "") return Number(item.amount) || 0;
  const qty = parseFloat(item?.qty);
  const rate = parseFloat(item?.rate);
  if (isNaN(qty) || isNaN(rate)) return 0;
  return qty * rate;
};

// Label sits in a fixed column so the labels line up down the page. Letting the
// value column size to its content instead pushes each label to a different
// place — a long customer name shunts its label left while short values leave
// theirs mid-row, which reads as misaligned.
const META_LABEL_WIDTH = 104;

const MetaRow = ({ label, value, first }) => (
  <div
    style={{
      display: "flex",
      alignItems: "baseline",
      gap: 18,
      fontSize: 12,
      padding: "5px 0",
      borderTop: first ? "none" : `1px solid ${LINE}`,
    }}
  >
    <span
      style={{
        color: MUTED,
        letterSpacing: "0.03em",
        width: META_LABEL_WIDTH,
        flexShrink: 0,
        textAlign: "right",
      }}
    >
      {label}
    </span>
    <span
      style={{
        fontWeight: 700,
        flex: 1,
        minWidth: 0,
        textAlign: "right",
        overflowWrap: "anywhere",
      }}
    >
      {value || "—"}
    </span>
  </div>
);

const Party = ({ tag, accentDark, children }) => (
  <div style={{ flex: 1, background: SHADE, border: `1px solid ${LINE}`, borderRadius: 8, padding: "14px 16px" }}>
    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", color: accentDark, marginBottom: 6 }}>
      {tag}
    </div>
    <div style={{ fontSize: 12.5, lineHeight: 1.6 }}>{children}</div>
  </div>
);

const Chip = ({ label, value }) => (
  <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
    <span style={{ fontWeight: 700, letterSpacing: "0.06em", color: MUTED, textTransform: "uppercase", fontSize: 11 }}>
      {label}
    </span>
    <span style={{ fontWeight: 700, fontSize: 13 }}>{value || "—"}</span>
  </div>
);

// Small uppercase caption above a line item's code / description.
const Caption = ({ children, marginTop }) => (
  <span
    style={{
      display: "block",
      fontSize: 9,
      fontWeight: 700,
      letterSpacing: "0.07em",
      textTransform: "uppercase",
      color: "#9aa2b1",
      marginBottom: 1,
      marginTop,
    }}
  >
    {children}
  </span>
);

// The heading above a block of the document, in the brand's accent.
const SectionLabel = ({ children, accentDark }) => (
  <div
    style={{
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      color: accentDark,
      marginBottom: 8,
    }}
  >
    {children}
  </div>
);

const TH = ({ children, align = "right", width }) => (
  <th
    style={{
      background: INK,
      color: "#fff",
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
      padding: "10px 12px",
      textAlign: align,
      width,
    }}
  >
    {children}
  </th>
);

const PoDocument = forwardRef(
  ({ order, company, attachments = [], variant = "detailed", showVendorName = true, showAttachments = true }, ref) => {
    // The letterhead belongs to the branch that issued the PO, not the parent
    // company — one tenant runs several branches, each with its own logo,
    // address and brand. Branch data can be incomplete, so every piece falls
    // back to the company record.
    const branch = order?.wo?.branch || null;
    const brand = resolveBrand(branch);
    const address = branch?.address || company?.address;
    const phone = branch?.phone || company?.phone;

    // Try the branch's own mark, then the company's. Held in state rather than
    // by reassigning img.src, which React would overwrite on the next render.
    // Memoised because both URL helpers append a cache-busting timestamp, so
    // recomputing every render would refetch the image endlessly.
    const logoSources = useMemo(
      () => [branch?.logo && branch?.id != null ? getBranchLogoUrl(branch.id) : null, getLogoUrl()].filter(Boolean),
      [branch?.id, branch?.logo]
    );
    const [logoStage, setLogoStage] = useState(0);
    useEffect(() => setLogoStage(0), [logoSources]);
    const logoSrc = logoSources[logoStage];

    const items = order?.poItems || [];
    // Ship-to is flat columns on the PO rather than a nested object. The card
    // prints only when an address was actually entered — Ship Via alone is a
    // shipping method, not a destination.
    const shipTo = {
      name: order?.shipToName,
      address: order?.shipToAddress,
      city: order?.shipToCity,
      state: order?.shipToState,
      zip: order?.shipToZip,
    };
    const hasShipTo = Object.values(shipTo).some(Boolean);
    const total = items.reduce((sum, item) => sum + lineAmount(item), 0);

    return (
      <div
        ref={ref}
        style={{
          width: 850,
          background: "#ffffff",
          padding: "48px 52px",
          boxSizing: "border-box",
          fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
          fontSize: 13,
          color: INK,
          WebkitFontSmoothing: "antialiased",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", paddingTop: 8 }}>
          <div style={{ width: brand.logoWidth, textAlign: "center", flexShrink: 0 }}>
            {/* Width-constrained with height auto so the mark keeps its own
                proportions — clamping the height shrinks it off-spec. */}
            {/* A branch without an uploaded logo 404s, so this falls through to
                the company mark and finally renders nothing. */}
            {logoSrc && (
              <img
                src={logoSrc}
                alt=""
                crossOrigin="anonymous"
                style={{ width: "100%", height: "auto", display: "block", margin: "0 auto" }}
                onError={() => setLogoStage((stage) => stage + 1)}
              />
            )}
            {(address || phone) && (
              <div style={{ fontSize: 12, lineHeight: 1.6, marginTop: 12, color: MUTED }}>
                {address && (
                  <>
                    {address}
                    <br />
                  </>
                )}
                {phone && <>Phone {phone}</>}
              </div>
            )}
          </div>

          <div style={{ width: 320, textAlign: "right" }}>
            <div
              style={{
                fontSize: 15,
                fontWeight: 700,
                letterSpacing: 2,
                color: MUTED,
                textTransform: "uppercase",
              }}
            >
              Purchase Order
            </div>
            <div
              style={{
                fontSize: 30,
                fontWeight: 800,
                color: brand.accentDark,
                letterSpacing: 1,
                margin: "2px 0 16px",
              }}
            >
              {order?.number || "—"}
            </div>
            <MetaRow first label="Date" value={order?.createdAt ? formatDate(order.createdAt, "M/D/YYYY") : ""} />
            {variant === "detailed" && order?.quoteNumber && (
              <MetaRow label="Quote Number" value={order.quoteNumber} />
            )}
            {variant === "simple" && <MetaRow label="Payment Type" value={paymentItems[order?.paymentType] || ""} />}
            {/* Who the work is for — distinct from Ship To, which is where the
                goods physically go. */}
            <MetaRow label="Customer" value={order?.wo?.customer?.companyName} />
            <MetaRow label="Issued By:" value={order?.issuedBy?.name} />
            <MetaRow label="Issued For:" value={order?.issuedUser?.name} />
          </div>
        </div>

        {/* Vendor / Ship To */}
        <div style={{ display: "flex", justifyContent: "space-between", gap: 24, marginTop: 34 }}>
          <Party tag="VENDOR" accentDark={brand.accentDark}>
            {showVendorName ? (
              <span style={{ fontWeight: 700 }}>{order?.vendor || "—"}</span>
            ) : (
              <span style={{ color: MUTED, fontStyle: "italic" }}>Not shown</span>
            )}
          </Party>
          {/* Ship To is the delivery destination — a warehouse, jobsite, branch
              or will-call counter. It is NOT the customer, so it is never
              filled in from customer data: printing the customer here would
              assert a delivery address that was never given. The customer
              appears on its own line in the header instead. */}
          <Party tag="SHIP TO" accentDark={brand.accentDark}>
            {hasShipTo ? (
              <>
                <span style={{ fontWeight: 700 }}>{shipTo.name}</span>
                {shipTo.name && <br />}
                {shipTo.address}
                {shipTo.address && <br />}
                {[shipTo.city, shipTo.state].filter(Boolean).join(", ")} {shipTo.zip}
              </>
            ) : (
              <span style={{ color: MUTED, fontStyle: "italic" }}>Not specified</span>
            )}
          </Party>
        </div>

        {/* Ship Via / Work Order */}
        <div style={{ display: "flex", gap: 24, marginTop: 22 }}>
          {order?.shipVia && <Chip label="Ship Via" value={String(order.shipVia).toUpperCase()} />}
          <Chip label="Work Order #" value={order?.wo?.number} />
        </div>

        {variant === "detailed" ? (
          <>
            <div style={{ marginTop: 16, border: `1px solid ${LINE}`, borderRadius: 8, overflow: "hidden" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>
                    <TH align="left" width="48%">
                      Item/Description
                    </TH>
                    <TH>Qty</TH>
                    <TH>Rate</TH>
                    <TH>Amount</TH>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan={4} style={{ padding: 12, fontSize: 12.5, color: MUTED }}>
                        No items
                      </td>
                    </tr>
                  ) : (
                    items.map((item, index) => {
                      // Zebra shading on even rows, matching the design.
                      const shaded = index % 2 === 1;
                      const cell = {
                        padding: 12,
                        fontSize: 12.5,
                        verticalAlign: "top",
                        borderBottom: `1px solid ${LINE}`,
                        background: shaded ? SHADE : undefined,
                      };
                      const num = {
                        ...cell,
                        textAlign: "right",
                        whiteSpace: "nowrap",
                        fontVariantNumeric: "tabular-nums",
                      };
                      return (
                        <tr key={item.id ?? index}>
                          <td style={cell}>
                            {item.itemNumber && (
                              <>
                                <Caption>Item #</Caption>
                                <span style={{ display: "block", fontWeight: 700, color: INK, marginBottom: 2 }}>
                                  {item.itemNumber}
                                </span>
                              </>
                            )}
                            {item.description && (
                              <>
                                <Caption marginTop={item.itemNumber ? 5 : undefined}>Description</Caption>
                                <span style={{ display: "block", color: MUTED, lineHeight: 1.5 }}>
                                  {item.description}
                                </span>
                              </>
                            )}
                          </td>
                          <td style={num}>{item.qty ?? ""}</td>
                          <td style={num}>{item.rate != null && item.rate !== "" ? formatMoney(item.rate) : ""}</td>
                          <td style={num}>{lineAmount(item) ? formatMoney(lineAmount(item)) : ""}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {total > 0 && (
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    minWidth: 320,
                    background: `linear-gradient(90deg, ${brand.accent} 0%, ${brand.accentDark} 100%)`,
                    color: "#fff",
                    borderRadius: 8,
                    padding: "14px 18px",
                    boxShadow: "0 4px 14px rgba(20,30,50,0.25)",
                  }}
                >
                  <span
                    style={{
                      flex: 1,
                      fontWeight: 700,
                      fontSize: 14,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                    }}
                  >
                    Total
                  </span>
                  <span style={{ fontWeight: 800, fontSize: 20, letterSpacing: 0.5 }}>${formatMoney(total)}</span>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <div style={{ marginTop: 34 }}>
              <SectionLabel accentDark={brand.accentDark}>Description</SectionLabel>
              <div style={{ fontSize: 12.5, lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
                {order?.description || "—"}
              </div>
            </div>

            {/* A simple PO's items are descriptions and nothing else — no
                quantities, no prices, no total. This is what the tech app
                creates, and what the server-rendered receipt printed before
                the document moved into the browser. */}
            {items.length > 0 && (
              <div style={{ marginTop: 26 }}>
                <SectionLabel accentDark={brand.accentDark}>Items</SectionLabel>
                <div style={{ border: `1px solid ${LINE}`, borderRadius: 8, overflow: "hidden" }}>
                  {items.map((item, index) => (
                    <div
                      key={item.id ?? index}
                      style={{
                        padding: "10px 12px",
                        fontSize: 12.5,
                        lineHeight: 1.5,
                        background: index % 2 === 1 ? SHADE : undefined,
                        borderBottom: index < items.length - 1 ? `1px solid ${LINE}` : "none",
                      }}
                    >
                      {item.description}
                      {item.attachment && (
                        <span style={{ color: MUTED, marginLeft: 8 }}>{getRealFileName(item.attachment)}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {showAttachments && attachments.length > 0 && (
          <div style={{ marginTop: 34 }}>
            <SectionLabel accentDark={brand.accentDark}>Attachments</SectionLabel>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 16, alignItems: "flex-start" }}>
              {/* Already inlined as data URIs by the caller, so the capture
                  never touches a cross-origin image. */}
              {attachments.map((attachment, index) => (
                <div
                  key={index}
                  style={{
                    border: `1px solid ${LINE}`,
                    borderRadius: 10,
                    padding: 14,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 8,
                    background: SHADE,
                  }}
                >
                  <img
                    src={attachment.dataUrl}
                    alt=""
                    style={{
                      width: 190,
                      borderRadius: 6,
                      display: "block",
                      background: "#fff",
                      boxShadow: "0 4px 14px rgba(20,30,50,0.16)",
                    }}
                  />
                  <span style={{ fontSize: 11.5, color: MUTED, fontWeight: 600 }}>{attachment.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }
);

export default PoDocument;
